/**
 * Forecast + ensemble layer.
 *
 * Wraps multiple per-pathogen + per-region simulator runs into an
 * ensemble. Each ensemble member is a tuple (simulator, parameter
 * draw). The ensemble produces:
 *   - Quantiles per day (median, 25/75, 2.5/97.5, etc.) for each
 *     compartment / outcome
 *   - Per-member trajectories
 *
 * The point of the ensemble: real forecasts (Reich Lab COVID Forecast
 * Hub pattern) average diverse simulators with different assumptions.
 * Our v0.3 ensemble layer is the mechanism for that.
 *
 * Scoring: CRPS (Continuous Ranked Probability Score) + log score
 * for retrospective forecast evaluation. Standard methodology.
 */

import type { EpiResult } from './types.js';

export interface EnsembleMember {
  /** Member label (e.g., 'SEIR β=0.32', 'SEAIR fA=0.4'). */
  label: string;
  /** Run output. */
  result: EpiResult;
}

export interface EnsembleQuantiles {
  /** Day indices (common across members, interpolated if needed). */
  t: number[];
  /** Per-day quantile values for one compartment. */
  q025: number[];
  q25: number[];
  median: number[];
  q75: number[];
  q975: number[];
}

/**
 * Aggregate multiple ensemble members into per-day quantiles for one
 * compartment. Assumes all members share the same time grid; pass
 * pre-interpolated results if not.
 */
export function ensembleQuantiles(
  members: readonly EnsembleMember[], compartment: string,
): EnsembleQuantiles {
  if (members.length === 0) throw new Error('ensembleQuantiles: at least one member required');
  const ref = members[0]!.result;
  const T = ref.t.length;
  for (const m of members) {
    if (m.result.t.length !== T) {
      throw new Error(`ensembleQuantiles: member '${m.label}' has different time grid (${m.result.t.length} vs ${T})`);
    }
    if (!m.result.compartments[compartment]) {
      throw new Error(`ensembleQuantiles: member '${m.label}' missing compartment '${compartment}'`);
    }
  }
  const q025: number[] = [], q25: number[] = [], median: number[] = [];
  const q75: number[] = [], q975: number[] = [];
  for (let i = 0; i < T; i++) {
    const values = members.map(m => m.result.compartments[compartment]![i]!).sort((a, b) => a - b);
    q025.push(quantile(values, 0.025));
    q25.push(quantile(values, 0.25));
    median.push(quantile(values, 0.5));
    q75.push(quantile(values, 0.75));
    q975.push(quantile(values, 0.975));
  }
  return { t: ref.t.slice(), q025, q25, median, q75, q975 };
}

function quantile(sortedValues: readonly number[], q: number): number {
  if (sortedValues.length === 0) return NaN;
  if (sortedValues.length === 1) return sortedValues[0]!;
  const pos = (sortedValues.length - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  if (lo === hi) return sortedValues[lo]!;
  return sortedValues[lo]! + (sortedValues[hi]! - sortedValues[lo]!) * (pos - lo);
}

/**
 * Continuous Ranked Probability Score for a forecast distribution
 * vs an observed value. Lower is better.
 *
 * For an ensemble represented by sample {x_1, ..., x_n}:
 *   CRPS ≈ mean_i |x_i - y| - 0.5 × mean_{i,j} |x_i - x_j|
 *
 * where y is the observed value. Returns 0 when the ensemble is
 * a perfect point mass at y.
 */
export function continuousRankedProbabilityScore(samples: readonly number[], observed: number): number {
  if (samples.length === 0) return NaN;
  const n = samples.length;
  let sumAbsDiffYX = 0;
  for (const x of samples) sumAbsDiffYX += Math.abs(x - observed);
  let sumAbsDiffXX = 0;
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      sumAbsDiffXX += Math.abs(samples[i]! - samples[j]!);
    }
  }
  return sumAbsDiffYX / n - 0.5 * sumAbsDiffXX / (n * n);
}

/**
 * Log score (negative log probability) — assesses ensemble forecast
 * accuracy by binning samples + computing -log(P[observed bin]).
 *
 * Use case: side-by-side scoring of multiple ensemble methods.
 */
export function logScore(
  samples: readonly number[], observed: number,
  /** Bin width to discretise the sample distribution. */
  binWidth: number,
): number {
  const observedBin = Math.floor(observed / binWidth);
  let countInBin = 0;
  for (const s of samples) {
    if (Math.floor(s / binWidth) === observedBin) countInBin++;
  }
  // Smoothing: add 1 to count to avoid log(0).
  const p = (countInBin + 1) / (samples.length + 1);
  return -Math.log(p);
}

/**
 * Compute calibration: for each predicted quantile q ∈ {0.1, 0.2, ...},
 * what fraction of observed values fall ≤ the q-th quantile of the
 * ensemble? Perfect calibration: 0.1 → 0.1, 0.5 → 0.5, etc.
 */
export interface CalibrationPoint {
  predictedQuantile: number;
  empiricalCoverage: number;
}

export function calibrationCurve(
  forecasts: readonly { samples: readonly number[]; observed: number }[],
  quantiles: readonly number[] = [0.1, 0.25, 0.5, 0.75, 0.9],
): CalibrationPoint[] {
  return quantiles.map(q => {
    let countBelow = 0;
    for (const f of forecasts) {
      const sorted = [...f.samples].sort((a, b) => a - b);
      if (sorted.length === 0) continue;
      const qVal = quantile(sorted, q);
      if (f.observed <= qVal) countBelow++;
    }
    return { predictedQuantile: q, empiricalCoverage: countBelow / forecasts.length };
  });
}
